import React from 'react';
import { User } from 'lucide-react';
import { getSeatsRemaining, getTripCapacity } from '../../utils/tripSchedule';
import { formatRidersProgress, getAdditionalRidersNeeded } from '../../config/sharedTransferRules';

function getSeatState(index, filled, minimum) {
  if (index < filled) return 'filled';
  if (index < minimum) return 'minimum';
  return 'available';
}

const SEAT_STYLES = {
  filled: 'bg-[#A44432] text-white border-[#A44432]',
  minimum: 'bg-amber-50 text-amber-700 border-amber-400 border-dashed',
  available: 'bg-white text-stone-300 border-stone-200',
};

const TripSeatFigures = ({ trip }) => {
  const capacity = getTripCapacity(trip);
  const filled = trip.seats_filled ?? 0;
  const minimum = trip.min_seat_count ?? 2;
  const remaining = getSeatsRemaining(trip);
  const moreNeeded = getAdditionalRidersNeeded(filled, minimum);
  const progressLabel = formatRidersProgress(filled, minimum);

  return (
    <div>
      <p className="text-[10px] text-stone-500 uppercase font-bold tracking-wider mb-2">
        Seats
      </p>
      <div
        className="flex flex-wrap gap-1.5"
        role="img"
        aria-label={`${filled} of ${capacity} seats filled, ${progressLabel}`}
      >
        {Array.from({ length: capacity }, (_, index) => {
          const state = getSeatState(index, filled, minimum);
          return (
            <div
              key={index}
              className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${SEAT_STYLES[state]}`}
              title={
                state === 'filled'
                  ? 'Seat taken'
                  : state === 'minimum'
                    ? 'Still needed'
                    : 'Available'
              }
            >
              <User size={14} strokeWidth={state === 'filled' ? 2.5 : 2} />
            </div>
          );
        })}
      </div>
      <p className="text-sm text-[#2D1E17] font-medium mt-2">
        {filled} of {capacity} seat{capacity !== 1 ? 's' : ''}
        <span className="text-stone-500 font-normal">
          {' '}
          · {progressLabel}
        </span>
      </p>
      {remaining > 0 && (
        <p className="text-sm text-[#A44432] font-semibold mt-1">
          {remaining} seat{remaining !== 1 ? 's' : ''} still available
        </p>
      )}
      {remaining <= 0 && (
        <p className="text-sm text-stone-500 font-semibold mt-1">No seats remaining</p>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[10px] text-stone-500">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-[#A44432] border border-[#A44432]" />
          Taken
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-amber-50 border border-dashed border-amber-400" />
          Minimum needed ({moreNeeded} more)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-white border border-stone-200" />
          Available
        </span>
      </div>
    </div>
  );
};

export default TripSeatFigures;
